import React from 'react';
import { useStyles } from './styles';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
    InputV2,
    ButtonV2,
    Typography,
    Box,
    ICONS_NAME,
    Icon,
} from '@/elements';
import { FiberManualRecord } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import routes from '../../../routes';
import AuthWrapper from '../../../components/global/Wrappers/authWrapper';
import { toast } from 'react-toastify';
import { SET_PASSWORD } from '../../../actions/update';
import { useMutation } from '@apollo/client';
import Preloader from '../../../components/global/Preloader';
import {List, ListItem, ListItemText} from "@mui/material";
import {useToggle} from "@hooks";

const ResetPassword = () => {
    const classes = useStyles();
   const navigate = useNavigate();
    const params = useLocation();
    const searchParams = new URLSearchParams(params.search);
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const [showDoubleNewPassword, setShowDoubleNewPassword] = useToggle(false);

    const [showPassword, setShowPassword] = useToggle(false)
    const [setPassword, { loading }] = useMutation(SET_PASSWORD, {
        fetchPolicy: 'no-cache',
        context: {
            plug: true,
        },
    });
    const onSubmit = inputData => {
        localStorage.removeItem('jwtToken');
        setPassword({
            variables: {
                newPassword: inputData.newPassword,
                email: email,
                resetToken: token,
                type: 'PARTNER_ACCOUNT_EMPLOYEE',
            },
        })
            .then(() => toast.success('Success'))
            .then(() => navigate('/'))
            .catch(err => toast.error(err.message));
    };

    const checkUppercase = /([A-Z])|([А-ЯЁ])/;
    const checkLowercase = /([a-z])|([а-яё])/;
    const checkNumber = /([0-9])/;
    const checkSpecialCharacter = /[^A-Za-zА-Яа-яёЁ0-9\s]/;

    const validationSchema = Yup.object().shape({
        newPassword: Yup.string()
            .required('The field is required')
            .min(8, 'New Password must be at least 8 characters')
            .max(100, 'Password longer than 100 characters is not allowed')
            .matches(checkUppercase, 'Password must have one uppercase character')
            .matches(checkLowercase, 'Password must have one lowercase character')
            .matches(checkNumber, 'Password must have one number')
            .matches(checkSpecialCharacter, 'Password must have one special character'),
        doubleNewPassword: Yup.string()
            .oneOf([Yup.ref('newPassword'), null], 'The passwords don`t match')
            .required('The field is required'),
    });

    const {
        handleSubmit,
        watch,
        formState: { errors },
        control,
    } = useForm({
        resolver: yupResolver(validationSchema),
    });

    const inputVariable = !watch('newPassword') ? '' : watch('newPassword');

    if (loading) return <Preloader />;

    return (
        <AuthWrapper>
            <Box justify="center" align="center" direction="column" gap="20px" width="100%">
            <Typography variant="h6">
                Reset password
            </Typography>

            <Typography variant="subtitle">Almost done, just enter your new password.</Typography>

            <Box justify="center" direction="column" gap="40px" width="100%">
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box direction="column" gap="10px">
                <Controller
                    name="newPassword"
                    control={control}
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                        <InputV2
                            isError={!!errors.newPassword}
                            type={showPassword ? 'text' : 'password'}
                            value={value}
                            label="New Password"
                            onChange={onChange}
                            placeholder={'Enter your password'}
                            helperText={errors?.newPassword?.message ?? ' '}
                            rightAdornment={
                                <Icon
                                    icon={showPassword ? ICONS_NAME.eyeOff : ICONS_NAME.eyeOn}
                                    cursor="pointer"
                                    onClick={setShowPassword}
                                />
                            }
                        />
                    )}
                />
                <List className={classes.list}>
                    <ListItem
                        className={`${classes.listItem} ${
                            inputVariable?.split('').length >= 8 ? classes.active : classes.disable
                        }`}
                    >
                        <FiberManualRecord fontSize="inherit" className={classes.listIcon} />
                        <ListItemText primary="8 characters minimum" />
                    </ListItem>
                    <ListItem
                        className={`${classes.listItem} ${
                            checkUppercase.test(inputVariable) ? classes.active : classes.disable
                        }`}
                    >
                        <FiberManualRecord fontSize="inherit" className={classes.listIcon} />
                        <ListItemText primary="one uppercase character" />
                    </ListItem>
                    <ListItem
                        className={`${classes.listItem} ${
                            checkLowercase.test(inputVariable) ? classes.active : classes.disable
                        }`}
                    >
                        <FiberManualRecord fontSize="inherit" className={classes.listIcon} />
                        <ListItemText primary="one lowercase character" />
                    </ListItem>
                    <ListItem
                        className={`${classes.listItem} ${
                            checkNumber.test(inputVariable) ? classes.active : classes.disable
                        }`}
                    >
                        <FiberManualRecord fontSize="inherit" className={classes.listIcon} />
                        <ListItemText primary="one number" />
                    </ListItem>
                    <ListItem
                        className={`${classes.listItem} ${
                            checkSpecialCharacter.test(inputVariable) ? classes.active : classes.disable
                        }`}
                    >
                        <FiberManualRecord fontSize="inherit" className={classes.listIcon} />
                        <ListItemText primary="one special character" />
                    </ListItem>
                </List>
                <Controller
                    name="doubleNewPassword"
                    control={control}
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                        <InputV2
                            type={showDoubleNewPassword ? 'text' : 'password'}
                            value={value}
                            label="Confirm New Password"
                            placeholder="Confirm your password"
                            onChange={onChange}
                            width="100%"
                            isError={!!errors.doubleNewPassword}
                            helperText={errors?.doubleNewPassword?.message ?? ' '}
                            rightAdornment={
                                <Icon
                                    icon={showDoubleNewPassword ? ICONS_NAME.eyeOff : ICONS_NAME.eyeOn}
                                    cursor="pointer"
                                    onClick={setShowDoubleNewPassword}
                                />
                            }
                        />
                    )}
                />
                <ButtonV2 width={"100%"} type="submit">
                    save
                </ButtonV2>
                </Box>
            </form>
            </Box>
            <Link to={routes.auth} className={classes.link}>
                Sign in with email
            </Link>
            </Box>
        </AuthWrapper>
    );
};

export default ResetPassword;
